import {
  AI_MODEL,
  WORKER_URL,
  AI_MAX_ATTEMPTS,
  AI_RETRY_DELAYS_MS,
  AI_PER_ATTEMPT_TIMEOUT_MS,
  AI_TEMPERATURE,
} from './constants';

// ─────────────────────────────────────────────────────────────────────────────
// Shared helper for every Claude Worker call. Owns URL, headers, retry loop,
// backoff, AbortController timeout, and response parsing per
// GRC_UI_PATTERNS.md §12.
//
// Return shape (discriminated):
//   { ok: true,  text, raw }
//   { ok: false, error: 'overloaded' | 'timeout' | 'network' |
//                       'parse_error' | 'content_filter' | 'invalid_request' }
//
// Retry policy per §12:
//   - overloaded, timeout, network, parse_error → retry up to AI_MAX_ATTEMPTS
//   - content_filter, invalid_request           → fail immediately
// ─────────────────────────────────────────────────────────────────────────────

const RETRYABLE = new Set(['overloaded', 'timeout', 'network', 'parse_error']);

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function attempt(prompt, maxTokens) {
  const controller = new AbortController();
  const timerId = setTimeout(() => controller.abort(), AI_PER_ATTEMPT_TIMEOUT_MS);
  let response;
  try {
    response = await fetch(WORKER_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Worker-Secret': process.env.REACT_APP_WORKER_SECRET,
      },
      body: JSON.stringify({
        model: AI_MODEL,
        max_tokens: maxTokens,
        temperature: AI_TEMPERATURE,
        messages: [{ role: 'user', content: prompt }],
      }),
      signal: controller.signal,
    });
  } catch (err) {
    if (err && err.name === 'AbortError') return { ok: false, error: 'timeout' };
    return { ok: false, error: 'network' };
  } finally {
    clearTimeout(timerId);
  }

  if (response.status === 400 || response.status === 422) {
    return { ok: false, error: 'invalid_request' };
  }

  let data;
  try {
    data = await response.json();
  } catch {
    return { ok: false, error: 'parse_error' };
  }

  if (data && data.type === 'error' && data.error && data.error.type === 'invalid_request_error') {
    return { ok: false, error: 'invalid_request' };
  }
  if (data && (data.type === 'error' || data.error)) {
    const msg = (data.error && (data.error.type || data.error.message)) || '';
    if (typeof msg === 'string' && /content[_ ]?filter|safety|blocked/i.test(msg)) {
      return { ok: false, error: 'content_filter' };
    }
    return { ok: false, error: 'overloaded' };
  }
  if (data && data.stop_reason && /content[_ ]?filter|safety/i.test(data.stop_reason)) {
    return { ok: false, error: 'content_filter' };
  }

  const text = Array.isArray(data && data.content)
    ? (data.content.find((b) => b && b.type === 'text') || {}).text
    : null;
  if (typeof text !== 'string' || text === '') {
    return { ok: false, error: 'parse_error' };
  }
  return { ok: true, text, raw: data };
}

/**
 * Call the Claude Worker with retry, backoff, and per-attempt timeout.
 *
 * @param {Object}  opts
 * @param {string}  opts.prompt     User message content. All prompts in English (§9).
 * @param {number}  opts.maxTokens  max_tokens value for this call.
 * @returns {Promise<{ok: true, text: string, raw: object} |
 *                    {ok: false, error: string}>}
 */
export async function callWorker({ prompt, maxTokens }) {
  let last = { ok: false, error: 'network' };
  for (let i = 0; i < AI_MAX_ATTEMPTS; i++) {
    const result = await attempt(prompt, maxTokens);
    if (result.ok) return result;
    last = result;
    if (!RETRYABLE.has(result.error)) return result;
    if (i < AI_MAX_ATTEMPTS - 1) {
      const delay = AI_RETRY_DELAYS_MS[i] != null
        ? AI_RETRY_DELAYS_MS[i]
        : AI_RETRY_DELAYS_MS[AI_RETRY_DELAYS_MS.length - 1];
      await sleep(delay);
    }
  }
  return last;
}
